/**
 * Blog Data Module
 *
 * Exports blog posts, authors, and helper functions
 */

export * from "./types";
export * from "./authors";

// Import all blog posts
import { welcomePost } from "./posts/welcome-to-builder-buddy";
import { howToUseChatPost } from "./posts/how-to-use-chat";
import { howToUseCodeLibraryPost } from "./posts/how-to-use-code-library";
import { howToUsePermitsPost } from "./posts/how-to-use-permits";
import { howToUseSdabPost } from "./posts/how-to-use-sdab";
import { howToUseQuantitySurveyPost } from "./posts/how-to-use-quantity-survey";
import { howToUseFeasibilityPost } from "./posts/how-to-use-feasibility";

import type { BlogPost, BlogCategory, AuthorSlug, BlogMeta } from "./types";

/**
 * All blog posts in chronological order (newest first)
 */
export const ALL_POSTS: BlogPost[] = [
  welcomePost,
  howToUseChatPost,
  howToUseCodeLibraryPost,
  howToUsePermitsPost,
  howToUseSdabPost,
  howToUseQuantitySurveyPost,
  howToUseFeasibilityPost,
].sort(
  (a, b) =>
    new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
);

/**
 * Get a blog post by slug
 */
export function getPostBySlug(slug: string): BlogPost | undefined {
  return ALL_POSTS.find((post) => post.slug === slug);
}

/**
 * Get posts by category
 */
export function getPostsByCategory(category: BlogCategory): BlogPost[] {
  return ALL_POSTS.filter((post) => post.category === category);
}

/**
 * Get posts by author
 */
export function getPostsByAuthor(author: AuthorSlug): BlogPost[] {
  return ALL_POSTS.filter((post) => post.author === author);
}

/**
 * Get posts by tag
 */
export function getPostsByTag(tag: string): BlogPost[] {
  return ALL_POSTS.filter((post) => post.tags.includes(tag));
}

/**
 * Get related posts for a given post
 */
export function getRelatedPosts(post: BlogPost, limit: number = 3): BlogPost[] {
  // If post has explicit related posts, use those
  if (post.relatedPosts && post.relatedPosts.length > 0) {
    return post.relatedPosts
      .map((slug) => getPostBySlug(slug))
      .filter((p): p is BlogPost => p !== undefined)
      .slice(0, limit);
  }

  // Otherwise, find posts with matching tags or category
  const related = ALL_POSTS.filter((p) => {
    if (p.slug === post.slug) return false;
    if (p.category === post.category) return true;
    return p.tags.some((tag) => post.tags.includes(tag));
  });

  return related.slice(0, limit);
}

/**
 * Get blog metadata
 */
export function getBlogMeta(): BlogMeta {
  const categories = [...new Set(ALL_POSTS.map((p) => p.category))];
  const authors = [...new Set(ALL_POSTS.map((p) => p.author))];
  const tags = [...new Set(ALL_POSTS.flatMap((p) => p.tags))];

  return {
    totalPosts: ALL_POSTS.length,
    categories,
    authors,
    tags,
  };
}

/**
 * Search posts by query
 */
export function searchPosts(query: string): BlogPost[] {
  const lowerQuery = query.toLowerCase();
  return ALL_POSTS.filter(
    (post) =>
      post.title.toLowerCase().includes(lowerQuery) ||
      post.description.toLowerCase().includes(lowerQuery) ||
      post.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
